import { FlashSaleCampaignName } from 'src/common/enums/campaigns';
import type { UserData } from 'src/common/interfaces';
import { type CampaignConfig, CampaignElementType } from 'src/common/interfaces/campaigns';
import { Applicable, CampaignType } from '../admin/campaigns/campaigns.types';
import { AfterSignupFlashSaleAccountCard } from './after-signup-flash-sale/account-card';
import { AfterSignupFlashSaleModal } from './after-signup-flash-sale/modal';
import { AfterSignupFlashSaleTopBar } from './after-signup-flash-sale/top-bar';
import { AnonymousSaleTopBar } from './anonymous-sale/components/anonymous-sale-top-bar';
import { CertificationsFlashSaleModal } from './certificates-flash-sale/modal';
import { CertificationsFlashSaleTopBar } from './certificates-flash-sale/top-bar';
import { CybersecurityAwarenessAccountCard } from './cyberawareness-flash-sale/account-card';
import { CybersecurityAwarenessModal } from './cyberawareness-flash-sale/modal';
import { CybersecurityAwarenessTopBar } from './cyberawareness-flash-sale/top-bar';
import { TicketingEventCampaignAccountCard } from './hack2win-flash-sale/account-card';
import { TicketingEventCampaignModal } from './hack2win-flash-sale/modal';
import { TicketingEventCampaignTopBar } from './hack2win-flash-sale/top-bar';
import { checkMatch } from './helpers';
import { InfluencerCampaignBar } from './influencers/components/influencer-campaign-bar/influencer-campaign-bar';
import { InfluencerCampaignModal } from './influencers/components/influencer-campaign-modal/influencer-campaign-modal';
import { InfluencerSubscriptionCampaignBar } from './influencers/components/influencer-subscription-campaign-bar/influencer-subscription-campaign-bar';
import { InfluencerSubscriptionCampaignModal } from './influencers/components/influencer-subscription-campaign-modal/influencer-subscription-campaign-modal';
import { FireshipModalContent } from './influencers/components/modal-content/fireship.modal';
import { SubscriptionContentModal } from './influencers/components/modal-content/subscription-content.modal';
export type CampaignConfigPerElement = { [key in CampaignElementType]?: CampaignConfig };
type CampaignTypesPerName = { [key in CampaignType]: (campaignName: string, isFlashSale?: boolean, applicableIf?: Applicable) => CampaignConfigPerElement };
const standardTopBarExclusionPathsCommon: string[] = ['/payment/pending', '/feedback/subscription', '/pricing', '/room/*', '/echo', '/badr', '/path/outline/*', '/recapme/*', '/certification/*', '/soc-sim/*', '/pre-signup', '/pre-signup-welcome', '/ttx', '/ttx/*', '/tabletop-exercises', '/tabletop-exercises/*'];
const standardTopBarExclusionPaths: string[] = [...standardTopBarExclusionPathsCommon, '/onboarding/*', '/welcome', '/login/*'];
const standardTopBarExclusionPathsForSubscriptions: string[] = [...standardTopBarExclusionPathsCommon, '/why-subscribe'];
const standardInfluencerTopBarInclusions: string[] = ['/dashboard/*'];
const standardInfluencerEmbeddedBarInclusions: string[] = ['/certification/junior-penetration-tester', '/certification/junior-penetration-tester/details', '/onboarding/*', '/welcome', '/login/*', '/signup/*'];
const standardInfluencerSubscriptionEmbeddedBarInclusions: string[] = ['/pricing', '/why-subscribe'];
const standardInfluencerModalInclusions: string[] = ['/certification/junior-penetration-tester/details*', '/room/*'];
const subscriptionFlashSaleTopBarShouldShow = (path: string, user?: UserData): boolean => {
  const isPremium = !!user?.user?.isPremium;
  const onManageSub = checkMatch(['/manage-account/subscription'], path).doesMatch;
  const onWhySub = checkMatch(['/why-subscribe'], path).doesMatch;
  if (onManageSub) return !isPremium;
  if (onWhySub) return isPremium;
  return true;
};
const subscriptionFlashSaleTopBarShouldShowIfFreeOrMonthly = (path: string, user?: UserData): boolean => {
  const isPremium = !!user?.user?.isPremium;
  const isFreeUser = !isPremium && !user?.user?.isPaused;
  const onManageSub = checkMatch(['/manage-account/subscription'], path).doesMatch;
  const onWhySub = checkMatch(['/why-subscribe'], path).doesMatch;
  const isMonthlyUser = isPremium && user.user.subscriptions.find(s => s.type === 'premium-monthly') !== undefined;
  if (onManageSub && (isFreeUser || isMonthlyUser)) return true;
  if (onWhySub && (isFreeUser || isMonthlyUser)) return false;
  return true;
};
const subscriptionCampaignTopBarShouldShow = (path: string, user?: UserData): boolean => {
  const isPremium = user?.user?.isPremium;
  const isOnManageSub = checkMatch(['/manage-account/subscription'], path).doesMatch;
  return isPremium ? !isOnManageSub : true;
};
const shouldShowIfNotPremium = (_: string, user?: UserData): boolean => !user?.user?.isPremium;
const ACCOUNT_AGE_ELIGIBILITY_MS = 24 * 60 * 60 * 1000;
const shouldShowIfNotPremiumAndNewlySignedUp = (_: string, user?: UserData): boolean => {
  if (!user?.user) return true; // Show to anonymous users
  if (user.user.isPremium) return false; // Hide from premium users

  const signUpDate = user.user.dateSignUp ? new Date(user.user.dateSignUp).getTime() : Number.POSITIVE_INFINITY;
  const isNewlySignedUp = Date.now() - signUpDate < ACCOUNT_AGE_ELIGIBILITY_MS;
  return isNewlySignedUp;
};
const shouldShowIfFreeOrMonthly = (_: string, user?: UserData): boolean => {
  if (!user) return false;
  const isPremium = user.user.isPremium;
  const isPaused = user.user.isPaused;
  const isMonthlyUser = user.user.subscriptions.find(s => s.type === 'premium-monthly') !== undefined;
  if (!isPremium || isPremium && !isPaused && isMonthlyUser) return true;
  return false;
};
const excludePaths = standardTopBarExclusionPaths.filter(path => !/^\/room\/?/.test(path));
const examCampaignConfig = (campaignName: string, isFlashSale?: boolean, __?: Applicable): CampaignConfigPerElement => {
  if (isFlashSale) {
    const eventFlashSales = {
      [FlashSaleCampaignName.FLASH_SALE_CERTIFICATIONS_2025]: {
        [CampaignElementType.TOP_BAR]: {
          renderComponent: (isPremium?: boolean) => <CertificationsFlashSaleTopBar hideCTA={false} isPremium={isPremium} />,
          pathsToExclude: [...standardTopBarExclusionPathsCommon, '/certifications']
        },
        [CampaignElementType.EMBEDDED_BAR]: {
          renderComponent: () => <CertificationsFlashSaleTopBar hideCTA />,
          pathsToInclude: ['/certifications']
        },
        [CampaignElementType.MODAL]: {
          renderComponent: () => <CertificationsFlashSaleModal />,
          pathsToInclude: ['/dashboard']
        }
      }
    };
    const flashSaleName = campaignName as keyof typeof eventFlashSales;
    if (eventFlashSales[flashSaleName]) return eventFlashSales[flashSaleName];

    // DO NOT REMOVE: Not implemented flash sale should not fallback to default implementation
    return {};
  }
  return {
    [CampaignElementType.TOP_BAR]: {
      renderComponent: () => <InfluencerCampaignBar influencerCampaign={campaignName} />,
      pathsToInclude: standardInfluencerTopBarInclusions
    },
    [CampaignElementType.EMBEDDED_BAR]: {
      renderComponent: () => <InfluencerCampaignBar influencerCampaign={campaignName} />,
      pathsToInclude: standardInfluencerEmbeddedBarInclusions
    },
    [CampaignElementType.MODAL]: {
      renderComponent: () => <InfluencerCampaignModal />,
      pathsToInclude: standardInfluencerModalInclusions,
      pathsToExclude: ['/']
    }
  };
};
const subscriptionCampaignConfig = (campaignName: string, isFlashSale?: boolean, applicableIf?: Applicable): CampaignConfigPerElement => {
  if (isFlashSale) {
    const eventFlashSales = {
      [FlashSaleCampaignName.FLASH_SALE_HACK2WIN_2025]: {
        [CampaignElementType.TOP_BAR]: {
          renderComponent: () => <TicketingEventCampaignTopBar />,
          pathsToExclude: excludePaths,
          shouldShow: subscriptionFlashSaleTopBarShouldShow
        },
        [CampaignElementType.EMBEDDED_BAR]: {
          renderComponent: () => <TicketingEventCampaignTopBar hideCTA />,
          pathsToInclude: ['/why-subscribe', '/pricing'],
          shouldShow: shouldShowIfNotPremium
        },
        [CampaignElementType.MODAL]: {
          renderComponent: () => <TicketingEventCampaignModal />,
          pathsToInclude: ['/dashboard']
        },
        [CampaignElementType.ACCOUNT_CARD]: {
          renderComponent: () => <TicketingEventCampaignAccountCard />
        }
      },
      [FlashSaleCampaignName.FLASH_SALE_CYBERAWARENESS_2025]: {
        [CampaignElementType.TOP_BAR]: {
          renderComponent: () => <CybersecurityAwarenessTopBar />,
          pathsToExclude: excludePaths,
          shouldShow: subscriptionFlashSaleTopBarShouldShow
        },
        [CampaignElementType.EMBEDDED_BAR]: {
          renderComponent: () => <CybersecurityAwarenessTopBar hideCTA />,
          pathsToInclude: ['/why-subscribe'],
          shouldShow: shouldShowIfNotPremium
        },
        [CampaignElementType.MODAL]: {
          renderComponent: () => <CybersecurityAwarenessModal />,
          pathsToInclude: ['/dashboard']
        },
        [CampaignElementType.ACCOUNT_CARD]: {
          renderComponent: () => <CybersecurityAwarenessAccountCard />
        }
      }
    };
    const flashSaleName = campaignName as keyof typeof eventFlashSales;
    if (eventFlashSales[flashSaleName]) return eventFlashSales[flashSaleName];
    const specialFlashSales = {
      [Applicable.HAS_NOT_SUBSCRIBED_AFTER_X_DAYS]: {
        [CampaignElementType.TOP_BAR]: {
          renderComponent: () => <AfterSignupFlashSaleTopBar />,
          pathsToExclude: excludePaths,
          shouldShow: subscriptionFlashSaleTopBarShouldShowIfFreeOrMonthly
        },
        [CampaignElementType.EMBEDDED_BAR]: {
          renderComponent: () => <AfterSignupFlashSaleTopBar hideCTA />,
          pathsToInclude: ['/why-subscribe'],
          shouldShow: shouldShowIfFreeOrMonthly
        },
        [CampaignElementType.MODAL]: {
          renderComponent: () => <AfterSignupFlashSaleModal />,
          pathsToInclude: ['/dashboard']
        },
        [CampaignElementType.ACCOUNT_CARD]: {
          renderComponent: () => <AfterSignupFlashSaleAccountCard />
        }
      }
    };
    const applicableFlashSale = applicableIf as keyof typeof specialFlashSales;
    if (applicableIf && specialFlashSales[applicableFlashSale]) {
      return specialFlashSales[applicableFlashSale];
    }

    // DO NOT REMOVE: Not implemented flash sale should not fallback to default implementation
    return {};
  }
  const customCampaigns = {
    FIRESHIP_2025: {
      [CampaignElementType.TOP_BAR]: {
        renderComponent: () => <InfluencerSubscriptionCampaignBar influencerCampaign={campaignName} />,
        pathsToInclude: ['/*'],
        pathsToExclude: standardTopBarExclusionPaths,
        shouldShow: subscriptionCampaignTopBarShouldShow
      },
      [CampaignElementType.MODAL]: {
        renderComponent: () => <InfluencerSubscriptionCampaignModal influencerCampaign={campaignName} content={FireshipModalContent} />,
        pathsToInclude: ['/', '/room/*']
      }
    },
    // TODO: Remove this campaign when STUDENT30_2025 campaign ends
    // HARDCODED BECAUSE we want an easy way to do it with time constraints
    STUDENT30_2025: {}
  };
  const customCampaignName = campaignName as keyof typeof customCampaigns;
  if (customCampaigns[customCampaignName]) return customCampaigns[customCampaignName];
  const hideInfluencerDetails = ['GOOGLE_2025', 'STALESIGNUP25OCT25_2025', 'REJOIN_2025', 'REDDIT_2025'].includes(campaignName);
  return {
    [CampaignElementType.TOP_BAR]: {
      renderComponent: () => <InfluencerSubscriptionCampaignBar influencerCampaign={campaignName} hideInfluencerDetails={hideInfluencerDetails} />,
      pathsToExclude: standardTopBarExclusionPathsForSubscriptions,
      shouldShow: subscriptionCampaignTopBarShouldShow
    },
    [CampaignElementType.EMBEDDED_BAR]: {
      renderComponent: () => <InfluencerSubscriptionCampaignBar influencerCampaign={campaignName} isEmbedded hideInfluencerDetails={hideInfluencerDetails} />,
      pathsToInclude: standardInfluencerSubscriptionEmbeddedBarInclusions
    },
    [CampaignElementType.MODAL]: {
      renderComponent: () => <InfluencerSubscriptionCampaignModal influencerCampaign={campaignName} content={SubscriptionContentModal} hideInfluencerDetails={hideInfluencerDetails} />,
      pathsToInclude: ['/', '/room/*']
    }
  };
};
export const CampaignTypes: CampaignTypesPerName = {
  [CampaignType.EXAM]: examCampaignConfig,
  [CampaignType.MULTIPLE_EXAMS]: examCampaignConfig,
  [CampaignType.SUBSCRIPTION]: subscriptionCampaignConfig
};
const pathsToMoveBarDown = ['/why-subscribe', '/onboarding'];

/**
 * Frontend-only anonymous sale campaign configuration.
 * This campaign is not managed through the backend campaign system.
 * It activates based on localStorage tracking via the useAnonymousSale hook.
 */
export const anonymousSaleConfig: CampaignConfigPerElement = {
  [CampaignElementType.TOP_BAR]: {
    renderComponent: () => <AnonymousSaleTopBar />,
    pathsToExclude: [...excludePaths, ...pathsToMoveBarDown],
    shouldShow: shouldShowIfNotPremium
  },
  [CampaignElementType.EMBEDDED_BAR]: {
    renderComponent: () => <AnonymousSaleTopBar hideCTA />,
    pathsToInclude: pathsToMoveBarDown,
    shouldShow: shouldShowIfNotPremium
  }
};